import { forwardRef, useState } from 'react'
import { DayPicker, DayPickerProps } from 'react-day-picker'
import { parseDDMMYYYY } from '../helpers'

interface KDayPickerProps {
  /** Whether the calendar popup is visible. */
  open: boolean
  /** Current field value as `DD.MM.YYYY` (or empty) — drives both selection and visible month. */
  value?: string | null
  /** Called with the picked day, or `null` when the selection is cleared. */
  onSelect: (date: Date | null) => void
  required?: boolean
  /** Days the user cannot pick (forwarded to `DayPicker`). */
  disabled?: DayPickerProps['disabled']
}

const popupStyle: React.CSSProperties = {
  position: 'absolute',
  zIndex: 1000,
  backgroundColor: 'white',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  borderRadius: '8px',
  padding: '10px',
  marginTop: '5px',
}

/**
 * Shared calendar popup for the `DD.MM.YYYY` text fields on the Stammdaten pages.
 * A fully typed date stays selected and pulls the calendar to its month; manual
 * navigation is held in local state until the typed value changes. The month is
 * derived during render, so no `useEffect` is needed to keep them in sync.
 *
 * The forwarded ref lands on the popup wrapper so callers can run their own
 * click-outside detection against it.
 */
const KDayPicker = forwardRef<HTMLDivElement, KDayPickerProps>(
  ({ open, value, onSelect, required, disabled }, ref) => {
    const typedDate = parseDDMMYYYY(value)
    const [browsedMonth, setBrowsedMonth] = useState(new Date(2000, 0))

    if (!open) return null

    return (
      <div ref={ref} className='date-picker-popup' style={popupStyle}>
        <DayPicker
          mode='single'
          selected={typedDate ?? undefined}
          month={typedDate ?? browsedMonth}
          onMonthChange={setBrowsedMonth}
          onSelect={(d: Date | undefined) => onSelect(d ?? null)}
          captionLayout='dropdown'
          startMonth={new Date(1900, 0)}
          endMonth={new Date()}
          disabled={disabled}
          required={required}
        />
      </div>
    )
  }
)

KDayPicker.displayName = 'KDayPicker'

export default KDayPicker
